import type { ReactNode } from "react";
import { FAQItem } from "@/lib/data/digital-signage-players";
import { env } from "../environment";

export interface TrustedRetailer {
  name: string;
  logoUrl: string;
}

export interface ImpactMetric {
  value: string;
  label: string;
}

export interface SmartFeature {
  iconUrl?: string;
  bgColor?: string;
  subHeading?: string;
  title: string;
  description: string | ReactNode;
  imageUrl: string;
  imageAlt: string;
}

export interface SolutionSection {
  title: string;
  description: string | ReactNode;
  imageUrl: string;
  imageAlt: string;
  linkText?: string;
  linkUrl?: string;
}

export const trustedRetailers: TrustedRetailer[] = [
  { name: "Amazon", logoUrl: "/amazon.8iCwzxJdzUH4.svg" },
  { name: "Asian Paints", logoUrl: "/asian-paint.bN-HdDp8BLDN.svg" },
  { name: "Decathlon", logoUrl: "/decathlon.D-02NO14pztY.svg" },
  { name: "Namdhari's Fresh", logoUrl: "/namdharis_fresh.gGLpocb_EqFq.svg" },
  { name: "Ek", logoUrl: "/brmagazacilik.3rxogV1TXSpK.svg" },
  { name: "Walmart", logoUrl: "/amazon.8iCwzxJdzUH4.svg" },
  { name: "Target", logoUrl: "/asian-paint.bN-HdDp8BLDN.svg" },
  { name: "Best Buy", logoUrl: "/decathlon.D-02NO14pztY.svg" },
  { name: "Home Depot", logoUrl: "/namdharis_fresh.gGLpocb_EqFq.svg" },
  { name: "Costco", logoUrl: "/brmagazacilik.3rxogV1TXSpK.svg" },
];

export const impactMetrics: ImpactMetric[] = [
  { value: "25%", label: "Increase in Sales" },
  { value: "2X", label: "Customer Engagement" },
  { value: "50%", label: "Reduction in Operational Costs" },
];

export const smartFeatures: SmartFeature[] = [
  {
    iconUrl: "/real-times.C9aPMS-Q2HAF.webp",
    bgColor: "bg-blue-100",
    subHeading: "Update & Sell Instantly",
    title: "Real-Time Promotions & Pricing",
    description:
      "<p>Integrate with your <a href='/industries/retail-digital-signage-software/pos-integration/' class='text-primary-hover'>POS system</a> to display real-time pricing, promotions, and product information, ensuring customers are always informed.</p>",
    imageUrl: "/real-times.C9aPMS-Q2HAF.webp",
    imageAlt: "Real-time promotions display",
  },
  {
    iconUrl: "/interactive-product.SZYNFFtT7mvG.webp",
    bgColor: "bg-green-100",
    subHeading: "Let Your Display Talk",
    title: "Interactive Product Catalogues",
    description:
      "Showcase your products with customizable <a href='/solutions/digital-product-catalogue/' class='text-primary-hover'>digital product catalogues</a>, complete with images, videos, and detailed descriptions, enhancing the shopping experience.",
    imageUrl: "/interactive-product.SZYNFFtT7mvG.webp",
    imageAlt: "Interactive product catalog display",
  },
  {
    iconUrl: "/customer-ffedback.EJIOo7gYW81-.webp",
    bgColor: "bg-purple-100",
    subHeading: "Turn Feedback into Sale",
    title: "Customer Feedback Integration",
    description:
      "Collect valuable customer feedback directly through digital signage, using <a href='/digital-signage-apps/qr-code' class='text-primary-hover'>QR codes</a> and interactive prompts to improve service and satisfaction. ",
    imageUrl: "/customer-ffedback.EJIOo7gYW81-.webp",
    imageAlt: "Customer feedback integration display",
  },
];

export const solutionSections: SolutionSection[] = [
  {
    title: "Hardware",
    description:
      "Attract shoppers with video walls, interactive kiosks and wayfinding boards. Pickcel’s retail digital signage works with your existing hardware and offers high compatibility with Android, Windows, & more.",
    imageUrl:
      env.NEXT_PUBLIC_CDN_URL +
      "/images/industries/retail-digital-signage-software/hardware.hvMXNfPNJ6ee.webp",
    imageAlt: "Retail digital signage hardware",
    linkText: "Find Hardware That Works",
    linkUrl: "/hardware",
  },
  {
    title: "Software",
    description:
      "Choose the solution that works with your retail operations– be it cloud based or a local setup. From small businesses to enterprise-level retail chains, Pickcel gives you the flexibility to scale without compromise.",
    imageUrl:
      env.NEXT_PUBLIC_CDN_URL +
      "/images/industries/retail-digital-signage-software/software.lutILSu4-aSQ.webp",
    imageAlt: "Retail software platforms",
  },
  {
    title: "Content Management System",
    description:
      "Manage all your retail messaging from one centralized platform. With intuitive scheduling and designing tools, Pickcel CMS helps you deliver consistent, on-brand content across every screen and store.",
    imageUrl:
      env.NEXT_PUBLIC_CDN_URL +
      "/images/industries/retail-digital-signage-software/cms-mangment-system.IKWAwoSPNkxt.webp",
    imageAlt: "Retail CMS dashboard",
    linkText: "Explore More on CMS",
    linkUrl: "/blog/what-is-a-content-management-system-for-digital-signage/",
  },
];

export const faqData: FAQItem[] = [
  {
    id: "1",
    question: "What is a retail digital display?",
    answer: `<p>Retail digital displays or retail advertising signs are strategically placed in various spots throughout the store to display relevant advertisements. They usually have a specific message and can either be highly visual or written. Businesses use these displays to show relevant messages that will draw the attention of potential customers. The signs are also used to inform customers about special offers and deals, product recalls, as well as other promotional campaigns.</p>`,
  },
  {
    id: "2",
    question: "Are my information and content secure?",
    answer: `<p>Yes, all your business data, graphs, confidential information, videos, images, and other details are secure in a cloud-based environment. You never have to worry about the security of your data at Pickcel.</p>`,
  },
  {
    id: "3",
    question: "Which file formats do you support?",
    answer: `<p>We support files in video (all except flash) and image (JPEG, Gif, Png) format. You can send your files in these formats. In case of any difficulty, feel free to contact us.</p>`,
  },
  {
    id: "4",
    question: "How does digital signage help in the retail business?",
    answer: `<p>With a digital signage solution from Pickcel, you can attract your set of audiences effectively and efficiently. You can make internet communication and let your customers know what better you can provide. No doubt, retail digital signage has enhanced businesses and the way they interact with their customers.</p>`,
  },
  {
    id: "5",
    question: "What signage is used in retail stores?",
    answer: `<p>Retail stores use a variety of signage to attract, inform, and guide customers. This includes promotional displays, digital signage, departmental signage, price tags, and informational posters. Digital signage in particular is increasingly popular due to its ability to dynamically update content and engage customers with multimedia presentations.</p>`,
  },
  {
    id: "6",
    question: "What are retail displays called?",
    answer: `<p>Retail displays are often referred to as store fixtures, display stands, merchandising units, or point-of-purchase (POP) displays. These can range from simple shelving systems to elaborate, themed end-caps and interactive kiosks.</p>`,
  },
  {
    id: "7",
    question: "How do retail stores display their merchandise? ",
    answer: `<p>Retail stores display their merchandise using a combination of shelving, hanging displays, window displays, mannequins, and interactive digital displays. The arrangement is typically designed to maximize product visibility and accessibility, encourage customer interaction, and align with the store’s branding and promotional strategies.</p>`,
  },
  {
    id: "8",
    question: "What makes a display attractive?",
    answer: `<p>An attractive display typically balances aesthetic appeal with functional design. Key elements include effective lighting, clear and engaging signage, thoughtful organization, and accessibility. Displays should capture the customer’s interest, reflect the brand’s image, and make the shopping experience intuitive and pleasant. Utilizing elements like color contrast, thematic storytelling, and interactive elements also significantly enhance the display's attractiveness.</p>`,
  },
];


